import type { Observable } from 'rxjs';
import { Subscription } from 'rxjs';
import { EventEmitter } from '@angular/core';
import type { NguiAutoCompleteNoMatchFoundMessage } from '@ngui/auto-complete';
import { unsubscribeIfActive } from '../../../../utils';
import { SpFormControl, SpFormControlType } from '../../sp-form-controls.model';
import type { CompositeChipsContainer } from './composite-chips-container.model';
import type { SpChipsDynamicPropertyMap } from './chips-dynamic-property-map.model';
import type { SpChipsControlParams } from './chips-form-input-control-params.model';

export class SpChipsInputControl extends SpFormControl {
    controlType = SpFormControlType.component;
    extComponent = 'ChipsComponent';

    allowExcessItems: boolean;
    showCounter: boolean;
    compositeModePosition?: CompositeChipsContainer;
    showCaret: boolean;

    suggestions?: any[];
    suggestionsProviderFunc?: (
        primaryIds: any[],
        secondaryIds?: any[],
    ) => Observable<any[]>;
    onBeforeAddFunc?: (newItem) => SpChipsDynamicPropertyMap;
    orderingFunc?: (a, b) => number;

    // outputs
    noMatchFoundEmitter: EventEmitter<any> = new EventEmitter();
    suggestionsLoaded: EventEmitter<any> = new EventEmitter();
    chipsInputFocus: EventEmitter<void>;

    private subscription: Subscription;

    constructor(params: SpChipsControlParams) {
        super(params);
        this.subscription = new Subscription();

        this.showCounter = !!params.showCounter;
        this.allowExcessItems =
            params.allowExcessItems === undefined
                ? false
                : params.allowExcessItems;
        this.compositeModePosition = params.compositeModePosition;
        this.showCaret = !!params.showCaret;
        this.suggestions = params.suggestions || [];
        this.suggestionsProviderFunc = params.suggestionsProviderFunc;
        this.onBeforeAddFunc = params.onBeforeAddFunc;
        this.orderingFunc = params.orderingFunc;
        this.chipsInputFocus = new EventEmitter<void>();

        if (
            (
                params.autocomplete
                    ?.noResultText as NguiAutoCompleteNoMatchFoundMessage
            )?.linkText
        ) {
            this.noMatchFoundEmitter = new EventEmitter();
        }
    }

    onDestroyFn() {
        unsubscribeIfActive(this.subscription);
    }

    loadInitialSuggestions(): void {
        if (this.suggestionsProviderFunc) {
            this.loadSuggestions(this._value);
            this.watchChanges();
            this.watchChipsInputFocus();
        }
    }

    watchChanges() {
        this.subscription.add(
            this.valueChange.subscribe((value) => this.loadSuggestions(value)),
        );
    }

    emitFocusEvent() {
        this.chipsInputFocus.next();
    }

    watchChipsInputFocus() {
        this.subscription.add(
            this.chipsInputFocus.subscribe(() =>
                this.loadSuggestions(this._value),
            ),
        );
    }

    protected loadSuggestions<T extends { id: number }>(
        value: T[] | undefined,
    ): void {
        const ids = (value || []).map((v) => v.id);

        if (ids.length && this.suggestionsProviderFunc) {
            this.suggestionsProviderFunc(ids).subscribe((suggestions) => {
                this.suggestions = suggestions;
                this.suggestionsLoaded.emit();
            });
        } else {
            this.suggestions = [];
            this.suggestionsLoaded.emit();
        }
    }
}
